import type { RecordView } from '@kit/sync/types/record-view';
import { AttachmentCount } from '@/entities/shared/attachment/attachment-count';
import { DueBadge } from '@/entities/shared/due/due-badge';
import { UrgencyPill } from '@/entities/shared/due/urgency-pill';
import { RecordTitle } from '@/entities/shared/record/record-title';
import { ListRow } from '@/shared/components/list/list-row';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatCents } from '@/shared/lib/money/format-cents';
import { formatQuantity } from '@/shared/lib/money/format-quantity';
import { isLowStock } from '../helpers/is-low-stock';
import { nextDue } from '../helpers/next-due';
import type { Item } from '../schemas/item';
import { ItemCover } from './item-cover';

type ItemRowProps = {
  readonly view: RecordView<Item>;
  readonly onOpen: () => void;
};

export function ItemRow({ view, onOpen }: ItemRowProps) {
  const { t } = useTranslation();
  const item = view.data;
  const meta = [
    formatQuantity(item.quantity, item.unit),
    item.location,
    item.price_cents != null ? formatCents(item.price_cents) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <ListRow
      onClick={onOpen}
      leading={<ItemCover item={item} />}
      title={<RecordTitle pending={view.pending}>{item.name}</RecordTitle>}
      subtitle={
        <span className="flex flex-col items-start gap-1">
          <span className="flex max-w-full items-center gap-2">
            <span className="truncate">{meta}</span>
            {/* One file is usually the photo already shown as the cover. */}
            {item.attachments.length > 1 && <AttachmentCount count={item.attachments.length} />}
          </span>
          <span className="flex flex-wrap gap-1">
            {isLowStock(item) && <UrgencyPill urgency="soon">{t('Low stock')}</UrgencyPill>}
            <DueBadge due={nextDue(item)} />
          </span>
        </span>
      }
    />
  );
}
